import { useState, useEffect } from 'react';
import { getPublishedTemplate } from '../../templates/api/templatesApi';

const FALLBACK_TEMPLATE = `AGREEMENT FOR SALE AND PURCHASE OF REAL PROPERTY
(Off-Plan Residential Unit)

THIS AGREEMENT is made on the date of execution below.

BETWEEN:

(1) [[developer_legal_name]] (NZBN: [[developer_nzbn]]) of [[developer_registered_address]] ("the Vendor")

AND

(2) [[buyer_full_name]] of [[buyer_address]] (Email: [[buyer_email]]) ("the Purchaser")

BACKGROUND:

A. The Vendor is the developer of [[project_name]] located at [[project_address]] ("the Development").

B. The Purchaser wishes to purchase Unit [[unit_number]] ([[unit_type]]) with an approximate internal area of [[unit_internal_area_sqm]] square metres (more or less) within the Development ("the Unit").

C. The Unit is to be created by survey and title issued upon deposit of the relevant unit plan with Land Information New Zealand.

IT IS AGREED:

1. SALE AND PURCHASE

1.1 The Vendor agrees to sell and the Purchaser agrees to purchase the Unit for the Purchase Price of NZD [[purchase_price]] (inclusive of GST, if any) ("the Purchase Price").

1.2 Any discount reflected in the Purchase Price was fixed prior to execution of this Agreement. No negotiation has been undertaken by Beaver.

2. DEPOSIT

2.1 The Purchaser shall pay a deposit of NZD [[deposit_amount]] ("the Deposit") within [[deposit_days]] working days of the date of this Agreement.

2.2 The Deposit shall be paid to [[developer_lawyer_name]] ("the Stakeholder") and held in the Stakeholder's trust account ([[developer_lawyer_trust_account]]) until: (a) all conditions are satisfied or waived; and (b) the Vendor is entitled to the Deposit under this Agreement.

2.3 The Deposit may be released early once conditions are satisfied, subject to lender requirements and the Stakeholder's undertaking.

3. CONDITIONS

3.1 Due Diligence: This Agreement is conditional upon the Purchaser completing due diligence investigations reasonably satisfactory to the Purchaser (acting reasonably) within [[due_diligence_days]] working days of the date of this Agreement. The Purchaser must give written notice of cancellation before expiry of this period.

3.2 Finance: This Agreement is conditional upon the Purchaser arranging finance on terms reasonably satisfactory to the Purchaser within [[finance_clause_days]] working days, including valuation. The Purchaser may cancel on valuation grounds only where the valuation is more than 10% below the Purchase Price and the Purchaser has used reasonable endeavours to obtain finance.

3.3 Sunset Date: If settlement has not occurred on or before [[sunset_date]] ("the Sunset Date"), either party may cancel this Agreement by written notice. The Vendor may extend the Sunset Date once only, for up to 6 months, where delay is caused by: (a) Council or utility authority delay; (b) weather events; (c) supply chain disruption; or (d) events beyond the Vendor's reasonable control. Written notice of any extension must be provided to the Purchaser.

4. OFF-PLAN PROVISIONS

4.1 The Unit is sold off-plan in accordance with the plans and specifications attached to this Agreement.

4.2 Variations: The Vendor may make variations that are: (a) reasonably required during construction; (b) required to comply with law, Building Code, or authority requirements; or (c) approved by engineers or professional consultants.

4.3 Material Variations: A variation is material only if it results in: (a) a reduction in internal area exceeding 5%; or (b) a change that materially and adversely affects the use or enjoyment of the Unit as a residential dwelling.

4.4 Remedy: If a material variation occurs, the Purchaser's sole remedy is a reasonable price adjustment determined by a registered valuer. Cancellation is available only where the Unit is rendered substantially different in character.

5. SETTLEMENT

5.1 Settlement shall occur 10 working days after: (a) issue of a Code Compliance Certificate applicable to the Unit; and (b) registration of the Unit title.

5.2 The Purchaser is entitled to one pre-settlement inspection to identify defects. Settlement shall not be delayed by minor defects or incomplete non-essential items.

6. DEFECTS AND WARRANTY

6.1 The Vendor warrants that the Unit has been constructed in a proper and workmanlike manner.

6.2 The Vendor shall remedy, within a reasonable time, any defects notified in writing within 12 months of settlement, excluding: (a) fair wear and tear; (b) damage caused by the Purchaser or third parties; and (c) cosmetic items not affecting use or compliance.

7. RISK AND INSURANCE

7.1 Risk in the Unit remains with the Vendor until settlement.

7.2 The Vendor must maintain full replacement insurance until settlement.

8. ASSIGNMENT

8.1 The Purchaser may assign this Agreement once only, with the Vendor's consent (not to be unreasonably withheld), subject to: (a) full disclosure of assignee details; (b) compliance with AML and lender requirements; and (c) payment of reasonable administrative costs.

9. DEFAULT

9.1 If the Purchaser defaults, the Vendor may cancel this Agreement, forfeit the Deposit (subject to law), and seek damages.

9.2 If the Vendor defaults, the Purchaser may cancel this Agreement, recover the Deposit, and pursue remedies available at law.

10. PLATFORM ACKNOWLEDGEMENT

10.1 The parties acknowledge that Beaver is a technology platform only, is not a real estate agent, is not a party to this Agreement, and that all rights and obligations arise solely between the Vendor and Purchaser.

11. GENERAL

11.1 This Agreement is governed by the laws of New Zealand.

11.2 Time is of the essence.

11.3 This Agreement constitutes the entire agreement between the parties.

EXECUTED as an Agreement:

VENDOR: ________________________________     Date: ________________

         [[developer_legal_name]]


PURCHASER: _____________________________     Date: ________________

           [[buyer_full_name]]`;

function formatCurrency(amount) {
  if (!amount) return '$0.00';
  return '$' + new Intl.NumberFormat('en-NZ', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return '[DATE TO BE CONFIRMED]';
  return new Date(dateStr).toLocaleDateString('en-NZ', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function renderTemplate(template, data) {
  let rendered = template;
  for (const [key, value] of Object.entries(data)) {
    const regex = new RegExp(`\\[\\[${key}\\]\\]`, 'g');
    rendered = rendered.replace(regex, String(value || '[NOT PROVIDED]'));
  }
  return rendered;
}

function convertToHtml(text) {
  const lines = text.split('\n');
  let html = '';

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      html += '<div class="h-3"></div>';
      continue;
    }

    if (trimmed === 'AGREEMENT FOR SALE AND PURCHASE OF REAL PROPERTY') {
      html += `<h1 class="text-center font-bold text-lg mb-1">${trimmed}</h1>`;
    } else if (trimmed === '(Off-Plan Residential Unit)') {
      html += `<p class="text-center text-sm mb-4">${trimmed}</p>`;
    } else if (trimmed.startsWith('THIS AGREEMENT') || trimmed.startsWith('BETWEEN:') || trimmed.startsWith('AND') || trimmed.startsWith('BACKGROUND:') || trimmed.startsWith('IT IS AGREED:') || trimmed.startsWith('EXECUTED')) {
      html += `<p class="font-bold mt-4 mb-2">${trimmed}</p>`;
    } else if (/^\d+\.\s+[A-Z]/.test(trimmed)) {
      html += `<p class="font-bold mt-4 mb-1">${trimmed}</p>`;
    } else if (/^\d+\.\d+/.test(trimmed)) {
      html += `<p class="ml-4 mb-2 text-justify">${trimmed}</p>`;
    } else if (trimmed.startsWith('(1)') || trimmed.startsWith('(2)')) {
      html += `<p class="ml-4 mb-2">${trimmed}</p>`;
    } else if (trimmed.startsWith('A.') || trimmed.startsWith('B.') || trimmed.startsWith('C.')) {
      html += `<p class="ml-4 mb-1">${trimmed}</p>`;
    } else if (trimmed.startsWith('VENDOR:') || trimmed.startsWith('PURCHASER:')) {
      html += `<p class="mt-6 mb-1">${trimmed}</p>`;
    } else {
      html += `<p class="mb-1">${trimmed}</p>`;
    }
  }

  return html;
}

export default function ContractViewModal({ asp, onClose }) {
  const [contractHtml, setContractHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [templateVersion, setTemplateVersion] = useState(null);

  useEffect(() => {
    loadTemplateAndGenerate();
  }, [asp]);

  async function loadTemplateAndGenerate() {
    setLoading(true);
    try {
      const template = await getPublishedTemplate('asp-agreement');
      const templateContent = template?.content || FALLBACK_TEMPLATE;
      setTemplateVersion(template?.version_number || null);
      generateContract(templateContent);
    } catch (err) {
      generateContract(FALLBACK_TEMPLATE);
    } finally {
      setLoading(false);
    }
  }

  function generateContract(templateContent) {
    const data = asp.agreement_data || {};
    const developer = data.developer || {};
    const buyer = data.buyer || {};
    const project = data.project || {};
    const unit = data.unit || {};
    const pricing = data.pricing || {};
    const dates = data.dates || {};
    const legal = data.legal || {};
    const stakeholder = legal.stakeholder || {};

    const templateData = {
      developer_legal_name: developer.legal_name,
      developer_nzbn: developer.nzbn,
      developer_registered_address: developer.registered_address,
      buyer_full_name: buyer.full_name,
      buyer_address: buyer.address,
      buyer_email: buyer.email,
      project_name: project.name,
      project_address: project.address,
      unit_number: unit.number,
      unit_type: unit.type,
      unit_internal_area_sqm: unit.internal_area_sqm,
      purchase_price: formatCurrency(pricing.purchase_price_nzd),
      deposit_amount: formatCurrency(pricing.deposit_amount_nzd),
      deposit_days: pricing.deposit_days || 10,
      sunset_date: formatDate(dates.sunset_date),
      due_diligence_days: asp.due_diligence_days || 10,
      finance_clause_days: asp.finance_clause_days || 15,
      developer_lawyer_name: stakeholder.lawyer_name,
      developer_lawyer_trust_account: stakeholder.trust_account,
    };

    const rendered = renderTemplate(templateContent, templateData);
    const html = convertToHtml(rendered);
    setContractHtml(html);
  }

  function handlePrint() {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Agreement for Sale & Purchase</title>
          <style>
            body {
              font-family: "Times New Roman", Times, serif;
              max-width: 700px;
              margin: 30px auto;
              padding: 20px;
              line-height: 1.4;
              font-size: 11pt;
            }
            h1 { font-size: 14pt; margin-bottom: 4px; }
            p { margin: 0 0 8px 0; text-align: justify; }
            .h-3 { height: 8px; }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .text-lg { font-size: 14pt; }
            .text-sm { font-size: 10pt; }
            .ml-4 { margin-left: 24px; }
            .mt-4 { margin-top: 16px; }
            .mt-6 { margin-top: 24px; }
            .mb-1 { margin-bottom: 4px; }
            .mb-2 { margin-bottom: 8px; }
            .mb-4 { margin-bottom: 16px; }
            .text-justify { text-align: justify; }
            @media print {
              body { margin: 0; padding: 40px; }
              @page { margin: 1.5cm; }
            }
          </style>
        </head>
        <body>${contractHtml}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200">
          <div>
            <h2 className="text-xl font-semibold text-secondary-900">
              Agreement for Sale & Purchase
            </h2>
            <p className="text-sm text-secondary-600 mt-1">
              Reference: {asp.id.slice(0, 8).toUpperCase()}
              {templateVersion && <span className="ml-2 text-secondary-400">(Template v{templateVersion})</span>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="btn-outline text-sm"
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="text-secondary-400 hover:text-secondary-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="bg-white shadow-sm border border-secondary-200 p-8 max-w-3xl mx-auto" style={{ fontFamily: '"Times New Roman", Times, serif' }}>
              <div
                className="contract-content text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: contractHtml }}
              />
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-secondary-200 flex justify-end">
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
